import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import './lectures-side-bar.css';
import { getHebrewYear } from '../../../utils/hebrew-date-utils';
import globalSettingsBranches from '../../../stores/global-settings-branches';
import { CustomExpander } from '../../../components/CustomExpander/CustomExpander';
import { FilterExpanderWithListOfCheckboxes } from '../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';
import globalSettingsStores from '../../../stores/settings-stores';
import { Button } from 'primereact/button';
import { AbstractProgram } from '../../../api/types/settings/studies/abstract-program';
import { LecturesSideBarFilterDatamodel } from './lectures-side-bar-filter-datamodel';
import {
  LectureStatus,
  getLectureStatusDisplayText,
} from '../../../api/types/programs/course/lectures/lecture-status.type';
import { CourseStatus } from '../../../api/types/programs/course/course-status';

interface YearOption {
  year: string;
  code: string;
  value: number;
}

interface LecturesSideBarProps {
  onSearch: (data: LecturesSideBarFilterDatamodel) => void;
  loading: boolean;
}

export const LecturesSideBar = observer(
  ({ loading, onSearch }: LecturesSideBarProps) => {
    const [selectedBranch, setSelectedBranch] = useState<number | null>(null);
    const [selectedPrograms, setSelectedPrograms] = useState<number[]>([]);
    const [selectedStartYear, setSelectedStartYear] = useState<number | null>(
      null,
    );
    const [selectedClasses, setSelectedClasses] = useState<number[]>([]);
    const [selectedLectureStates, setSelectedLectureStates] = useState<
      LectureStatus[]
    >([]);
    const [selectedCourses, setSelectedCourses] = useState<number[]>([]);
    const [selectedCourseStatuses, setSelectedCourseStatuses] = useState<
      CourseStatus[]
    >([]);

    // Generate years array
    const years: YearOption[] = [];
    for (let year = 1980; year <= 2030; year++) {
      years.push({
        year: `${getHebrewYear(year)} (${year} - ${year + 1})`,
        code: 'year',
        value: year,
      });
    }

    // Get all classes from all branches
    const getAllClasses = () => {
      const classes: { name: string; id: number }[] = [];
      globalSettingsBranches.branches?.forEach((branch) => {
        branch.buildings?.forEach((building) => {
          building.classes?.forEach((classItem) => {
            classes.push({
              name: `${branch.branchName} - ${building.buildingName} - ${classItem.className}`,
              id: classItem.id,
            });
          });
        });
      });
      return classes;
    };

    const styles = {
      container: {
        height: '100%',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column' as const,
      },
      header: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        marginBottom: '16px',
        color: '#333',
      },
      content: {
        flex: 1,
        overflowY: 'auto' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '16px',
        paddingRight: '4px',
      },
      dropdownContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
      },
      dropdownLabel: {
        fontSize: '0.9rem',
        color: '#666',
      },
      dateRangeContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        padding: '8px',
      },
      calendarWrapper: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
      },
      calendarLabel: {
        fontSize: '0.9rem',
        color: '#666',
      },
    };

    const handleSearch = () => {
      if (!selectedBranch) return;

      onSearch({
        branchId: selectedBranch,
        programs: selectedPrograms,
        startYear: selectedStartYear,
        classes: selectedClasses,
        lectureState: selectedLectureStates,
        courses: selectedCourses,
        courseStatuses: selectedCourseStatuses,
      });
    };

    return (
      <div style={styles.container}>
        <div style={styles.header}>סינונים</div>
        <div style={styles.content}>
          <CustomExpander>
            <div>שלוחה</div>
            <div style={styles.dropdownContainer}>
              <Dropdown
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.value)}
                options={globalSettingsBranches.branches}
                optionLabel="branchName"
                optionValue="id"
                placeholder="בחר שלוחה"
                style={{ width: '100%' }}
              />
            </div>
          </CustomExpander>

          <FilterExpanderWithListOfCheckboxes
            title="שם הקורס"
            items={globalSettingsStores.abstractCourses}
            selectedItems={selectedCourses}
            onItemToggle={(courseId) => {
              setSelectedCourses((prev) =>
                prev.includes(courseId)
                  ? prev.filter((c) => c !== courseId)
                  : [...prev, courseId],
              );
            }}
            getItemLabel={(course) => course.courseName}
            getItemKey={(course) => course.id}
            searchPlaceholder="חפש קורס..."
          />

          <FilterExpanderWithListOfCheckboxes<AbstractProgram, number>
            title="תכניות לימודים"
            items={globalSettingsStores.abstractProgram}
            selectedItems={selectedPrograms}
            onItemToggle={(programId) => {
              setSelectedPrograms((prev) =>
                prev.includes(programId)
                  ? prev.filter((p) => p !== programId)
                  : [...prev, programId],
              );
            }}
            getItemLabel={(program) => program.programName}
            getItemKey={(program) => program.id}
            searchPlaceholder="חפש תכנית..."
          />

          <CustomExpander>
            <div>שנת התחלה של התוכנית</div>
            <div style={styles.dropdownContainer}>
              <Dropdown
                value={selectedStartYear}
                onChange={(e) => setSelectedStartYear(e.value)}
                options={years}
                optionLabel="year"
                placeholder="בחר שנה"
                style={{ width: '100%' }}
              />
            </div>
          </CustomExpander>

          <FilterExpanderWithListOfCheckboxes<
            { name: string; id: number },
            number
          >
            title="כיתות"
            items={getAllClasses()}
            selectedItems={selectedClasses}
            onItemToggle={(classId) => {
              setSelectedClasses((prev) =>
                prev.includes(classId)
                  ? prev.filter((c) => c !== classId)
                  : [...prev, classId],
              );
            }}
            getItemLabel={(classItem) => classItem.name}
            getItemKey={(classItem) => classItem.id}
            searchPlaceholder="חפש כיתה..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="סטטוס קורס"
            items={Object.values(CourseStatus).filter(
              (value) => typeof value === 'string',
            )}
            selectedItems={selectedCourseStatuses}
            onItemToggle={(status) => {
              setSelectedCourseStatuses((prev) =>
                prev.includes(status as CourseStatus)
                  ? prev.filter((s) => s !== status)
                  : [...prev, status as CourseStatus],
              );
            }}
            getItemLabel={(status) => {
              return CourseStatus.getDisplayText(status as CourseStatus);
            }}
            getItemKey={(status) => status}
            searchPlaceholder="חפש סטטוס..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="סטטוס מפגש"
            items={Object.values(LectureStatus).filter(
              (value) => typeof value === 'string',
            )}
            selectedItems={selectedLectureStates}
            onItemToggle={(status) => {
              setSelectedLectureStates((prev) =>
                prev.includes(status as LectureStatus)
                  ? prev.filter((s) => s !== status)
                  : [...prev, status as LectureStatus],
              );
            }}
            getItemLabel={(status) => {
              return getLectureStatusDisplayText(status as LectureStatus);
            }}
            getItemKey={(status) => status}
            searchPlaceholder="חפש סטטוס..."
          />
        </div>
        <Button
          severity="success"
          onClick={handleSearch}
          style={{ width: '100%', marginTop: '16px' }}
          loading={loading}
          disabled={!selectedBranch}
        >
          חפש
        </Button>
      </div>
    );
  },
);
